/**
 * icd10HintEngine.js
 * --------------------
 * Symptom-to-ICD-10 hint engine. Detects when the LLM picked a code that
 * disagrees with the symptoms in the dictated notes — for example, when
 * notes mention "dysmenorrhea" but the LLM returned E28.2 (PCOS) instead
 * of N94.6 (Dysmenorrhea, unspecified).
 *
 * Each mapping declares:
 *   symptomKeywords: substrings to scan the dictated notes for (case-insensitive)
 *   preferredCode:    the canonical ICD-10 the symptom should map to
 *   preferredDesc:    human-readable description shown in the hint
 *   confusableCodes:  list of codes the LLM commonly picks instead
 *   confusableLabel:  short description of the confusable family (optional)
 *
 * Two confidence tiers:
 *   - HIGH (`wrong_code`): symptom present in notes AND a confusable code is
 *     in the suggestion list AND the preferred code is NOT. Recommends a SWAP.
 *   - MEDIUM (`missing_code`): symptom present in notes, preferred code not
 *     in suggestion list, no confusable detected. Recommends an ADD.
 *
 * Add new mappings whenever the LLM is repeatedly seen mis-coding a topic.
 * Keep the symptom keyword list specific — overly-broad keywords (e.g.
 * `"pain"`) produce noisy hints.
 */

export const SYMPTOM_CODE_HINTS = [
  // === GYN / Women's health (the original Feb-16 bug class) ===
  {
    symptomKeywords: ['dysmenorrhea', 'painful period', 'painful periods', 'menstrual cramp', 'period pain'],
    preferredCode: 'N94.6',
    preferredDesc: 'Dysmenorrhea, unspecified',
    confusableCodes: ['E28.2', 'N94.0'],
    confusableLabel: 'PCOS / ovulation pain',
  },
  {
    symptomKeywords: ['dyspareunia', 'painful intercourse', 'painful sex'],
    preferredCode: 'N94.1',
    preferredDesc: 'Dyspareunia',
    confusableCodes: ['N94.6', 'N94.4', 'N94.5'],
    confusableLabel: 'dysmenorrhea family',
  },
  {
    symptomKeywords: ['amenorrhea', 'missed period', 'no period', 'absent period'],
    preferredCode: 'N91.2',
    preferredDesc: 'Amenorrhea, unspecified',
    confusableCodes: ['E28.2'],
  },
  {
    symptomKeywords: ['pcos', 'polycystic ovary', 'polycystic ovarian'],
    preferredCode: 'E28.2',
    preferredDesc: 'Polycystic ovarian syndrome',
    confusableCodes: ['N94.6', 'N94.1'],
  },
  {
    symptomKeywords: ['heavy period', 'menorrhagia', 'heavy menses', 'heavy menstrual'],
    preferredCode: 'N92.0',
    preferredDesc: 'Excessive and frequent menstruation with regular cycle',
    confusableCodes: ['N92.1', 'N94.6'],
  },
  {
    symptomKeywords: ['postmenopausal bleeding', 'pmb'],
    preferredCode: 'N95.0',
    preferredDesc: 'Postmenopausal bleeding',
    confusableCodes: ['N95.1', 'N92.0'],
  },
  {
    symptomKeywords: ['hot flash', 'menopausal symptoms', 'perimenopause', 'climacteric'],
    preferredCode: 'N95.1',
    preferredDesc: 'Menopausal and female climacteric states',
    confusableCodes: ['F32.9', 'F41.1'],
  },

  // === Derm ===
  {
    symptomKeywords: ['eczema', 'atopic dermatitis'],
    preferredCode: 'L20.9',
    preferredDesc: 'Atopic dermatitis, unspecified',
    confusableCodes: ['L30.9', 'L23.9', 'L25.9'],
  },
  {
    symptomKeywords: ['contact dermatitis', 'allergic rash from'],
    preferredCode: 'L23.9',
    preferredDesc: 'Allergic contact dermatitis, unspecified cause',
    confusableCodes: ['L25.9', 'L30.9', 'L20.9'],
  },
  {
    symptomKeywords: ['acne'],
    preferredCode: 'L70.9',
    preferredDesc: 'Acne, unspecified',
    confusableCodes: ['L71.9'],
  },
  {
    symptomKeywords: ['rosacea'],
    preferredCode: 'L71.9',
    preferredDesc: 'Rosacea, unspecified',
    confusableCodes: ['L70.9'],
  },

  // === GU / UTI ===
  {
    symptomKeywords: ['uti', 'urinary tract infection', 'bladder infection'],
    preferredCode: 'N39.0',
    preferredDesc: 'Urinary tract infection, site not specified',
    confusableCodes: ['N30.00', 'R30.0', 'R31.9'],
  },
  {
    symptomKeywords: ['dysuria', 'burning urination', 'painful urination'],
    preferredCode: 'R30.0',
    preferredDesc: 'Dysuria',
    confusableCodes: ['N39.0', 'N30.00'],
  },

  // === Mental health ===
  {
    symptomKeywords: ['generalized anxiety', 'gad', 'anxiety disorder'],
    preferredCode: 'F41.1',
    preferredDesc: 'Generalized anxiety disorder',
    confusableCodes: ['F41.9', 'F32.9', 'F33.0', 'F43.10'],
  },
  {
    symptomKeywords: ['ptsd', 'post traumatic', 'post-traumatic stress'],
    preferredCode: 'F43.10',
    preferredDesc: 'Post-traumatic stress disorder, unspecified',
    confusableCodes: ['F41.1', 'F32.9'],
  },
  {
    symptomKeywords: ['insomnia', 'cannot sleep', 'difficulty sleeping', 'sleep difficulty'],
    preferredCode: 'F51.01',
    preferredDesc: 'Primary insomnia',
    confusableCodes: ['F51.09', 'F41.1'],
  },

  // === MSK (common confusables) ===
  {
    symptomKeywords: ['low back pain', 'lbp', 'lumbar pain', 'lumbago'],
    preferredCode: 'M54.50',
    preferredDesc: 'Low back pain, unspecified',
    confusableCodes: ['M54.41', 'M54.42', 'M54.6'],
  },
  {
    symptomKeywords: ['sciatica'],
    preferredCode: 'M54.41',
    preferredDesc: 'Lumbago with sciatica, right side',
    confusableCodes: ['M54.42', 'M54.50'],
  },

  // === Endo ===
  {
    symptomKeywords: ['vitamin d deficiency', 'low vitamin d', 'low vit d', '25-oh'],
    preferredCode: 'E55.9',
    preferredDesc: 'Vitamin D deficiency, unspecified',
    confusableCodes: ['E61.1'],
  },
  {
    symptomKeywords: ['iron deficiency', 'low iron', 'low ferritin'],
    preferredCode: 'E61.1',
    preferredDesc: 'Iron deficiency',
    confusableCodes: ['E55.9'],
  },
];

/**
 * detectIcd10Hints
 * ----------------
 * @param {string}   notesText      The provider's raw dictation/typed notes.
 * @param {Array}    currentCodes   Array of {code, description} currently
 *                                  attached to this panel (locked codes +
 *                                  LLM suggestions combined).
 * @returns {Array<{type, suggestedCode, suggestedDesc, currentCode?, currentDesc?, matchedSymptom}>}
 *
 * `type` is either `'wrong_code'` (high confidence; swap recommended) or
 * `'missing_code'` (medium confidence; add recommended).
 */
export const detectIcd10Hints = (notesText, currentCodes = []) => {
  if (!notesText || typeof notesText !== 'string') return [];
  const lower = notesText.toLowerCase();
  const codeSet = new Set(
    (currentCodes || []).map((c) => (c?.code || '').toUpperCase())
  );
  const hints = [];

  for (const mapping of SYMPTOM_CODE_HINTS) {
    const matched = mapping.symptomKeywords.find((kw) => lower.includes(kw));
    if (!matched) continue;
    const preferred = mapping.preferredCode.toUpperCase();
    if (codeSet.has(preferred)) continue; // already there

    const confusableHit = (mapping.confusableCodes || []).find((c) =>
      codeSet.has(c.toUpperCase())
    );
    if (confusableHit) {
      const currentObj = currentCodes.find(
        (c) => (c?.code || '').toUpperCase() === confusableHit.toUpperCase()
      );
      hints.push({
        type: 'wrong_code',
        matchedSymptom: matched,
        suggestedCode: mapping.preferredCode,
        suggestedDesc: mapping.preferredDesc,
        currentCode: confusableHit,
        currentDesc: currentObj?.description || mapping.confusableLabel || '',
      });
    } else if (currentCodes.length > 0) {
      hints.push({
        type: 'missing_code',
        matchedSymptom: matched,
        suggestedCode: mapping.preferredCode,
        suggestedDesc: mapping.preferredDesc,
      });
    }
  }
  return hints;
};

export default detectIcd10Hints;
